/**
 * Lightweight client for the marketing "analytics_serve_*" Cloud Function endpoints.
 *
 * These endpoints are separate from the main Bloom IQ app API (VITE_API_BASE_URL).
 * They are used by the marketing Analytics Tab; calls include the Firebase ID token when logged in.
 */

import { getAuth } from 'firebase/auth';
import { authApp } from '../firebase';

// GOTCHA: import.meta.env.DEV tracks NODE_ENV and is FALSE in ALL vite
// builds, including --mode development. DEV may only gate the
// vite-dev-server -> proxy case; built bundles must branch on
// import.meta.env.MODE. The old shape here ("DEV ? proxy : prodDefault")
// made PROD builds call the DEV analytics host — do not reintroduce it.
const ANALYTICS_SERVE_CLOUD_DEV =
  'https://us-central1-bloom-webapp-infra-dev.cloudfunctions.net';
const ANALYTICS_SERVE_CLOUD_PROD =
  'https://us-central1-bloom-webapp-infra-prod.cloudfunctions.net';
const defaultServeBase =
  import.meta.env.MODE === 'production' ? ANALYTICS_SERVE_CLOUD_PROD : ANALYTICS_SERVE_CLOUD_DEV;

// In local dev, '' routes through the Vite proxy to avoid CORS.
// In built bundles, use the real Cloud Function host by MODE (or override via env).
export const ANALYTICS_SERVE_BASE =
  (import.meta.env.DEV ? '' : (import.meta.env.VITE_ANALYTICS_SERVE_BASE_URL || defaultServeBase));

function withQuery(url, params) {
  // Relative paths (Vite dev proxy: "/analytics_overview_endpoint") need a base for URL().
  const isAbsolute = /^https?:\/\//i.test(url);
  const origin =
    typeof globalThis.location?.origin === 'string' && globalThis.location.origin
      ? globalThis.location.origin
      : 'http://localhost';
  const u = isAbsolute ? new URL(url) : new URL(url.startsWith('/') ? url : `/${url}`, origin);
  Object.entries(params || {}).forEach(([k, v]) => {
    if (v == null || v === '') return;
    u.searchParams.set(k, String(v));
  });
  return u.toString();
}

async function serveRequestHeaders() {
  const headers = { Accept: 'application/json' };
  try {
    const auth = getAuth(authApp);
    if (auth.currentUser) {
      const token = await auth.currentUser.getIdToken();
      headers.Authorization = `Bearer ${token}`;
    }
  } catch {
    // no session or token failure — backend may return 401
  }
  return headers;
}

async function fetchJson(url, { signal } = {}) {
  const res = await fetch(url, {
    method: 'GET',
    signal,
    headers: await serveRequestHeaders(),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Serve endpoint ${res.status}: ${text || res.statusText}`);
  }
  return res.json();
}

export function analyticsServeUrl(endpoint, commodityKey) {
  const base = ANALYTICS_SERVE_BASE.replace(/\/+$/, '');
  const ep = String(endpoint || '').replace(/^\/+/, '');
  const url = base ? `${base}/${ep}` : `/${ep}`;
  return commodityKey ? withQuery(url, { commodity: commodityKey }) : url;
}

function serveEndpointAbsoluteUrl(endpoint) {
  const base = ANALYTICS_SERVE_BASE.replace(/\/+$/, '');
  const ep = String(endpoint || '').replace(/^\/+/, '');
  if (base) return `${base}/${ep}`;
  const origin =
    typeof globalThis.location?.origin === 'string' && globalThis.location.origin
      ? globalThis.location.origin
      : 'http://localhost';
  return new URL(`/${ep}`, origin).toString();
}

/**
 * Some Cloud Functions read JSON from the body (same as VITE_API_BASE_URL scorecard endpoints).
 * GET + query string yields "Request was empty" on those handlers.
 */
async function fetchServePostJson(endpoint, body, { signal } = {}) {
  const url = serveEndpointAbsoluteUrl(endpoint);
  const headers = await serveRequestHeaders();
  headers['Content-Type'] = 'application/json';
  const res = await fetch(url, {
    method: 'POST',
    signal,
    headers,
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    const err = new Error(`Serve endpoint ${res.status}: ${text || res.statusText}`);
    throw err;
  }
  const data = await res.json();
  return data;
}

function buildScorecardStyleBody(commodityKey, opts = {}) {
  const { email, dateStart, dateEnd } = opts;
  const body = { commodity: commodityKey };
  if (email) body.email = email;
  if (dateStart) body.date_start = dateStart;
  if (dateEnd) body.date_end = dateEnd;
  return body;
}

export async function fetchAnalyticsServe(endpoint, commodityKey, { signal } = {}) {
  const url = analyticsServeUrl(endpoint, commodityKey);
  return fetchJson(url, { signal });
}

// ---- endpoints (documented by user) ----
export const serveCommodities = (opts) =>
  fetchAnalyticsServe('analytics_serve_commodities', null, opts);

export const serveFobPricePulse = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_fob_price_pulse', commodityKey, opts);

export const serveTerminalPricePulse = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_terminal_price_pulse', commodityKey, opts);

export const serveVolumeByOrigin = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_volume_by_origin', commodityKey, opts);

export const serveSupplyOptions = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_supply_options', commodityKey, opts);

export const serveWeatherAlerts = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_weather_alerts', commodityKey, opts);

export const serveArrivingNext10d = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_arriving_next_10d', commodityKey, opts);

export const serveActiveShipments = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_active_shipments', commodityKey, opts);

export const serveProducingRegions = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_producing_regions', commodityKey, opts);

export const serveAvgFobYear = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_avg_fob_year', commodityKey, opts);

export const serveSmartClassifier = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_smart_classifier', commodityKey, opts);

export const serveSmartTracker = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_smart_tracker', commodityKey, opts);

export const serveCommodityPlanner = (commodityKey, opts) =>
  fetchAnalyticsServe('analytics_serve_commodity_planner', commodityKey, opts);

// New (Alek): page-level overview strip (4 tiles)
export const serveOverview = (commodityKey, opts = {}) => {
  const { signal, ...rest } = opts;
  return fetchServePostJson(
    'analytics_overview_endpoint',
    buildScorecardStyleBody(commodityKey, rest),
    { signal },
  );
};

// New (Alek): producing regions scorecards (3 tiles)
export const serveProducingRegionsScorecards = (commodityKey, opts = {}) => {
  const { signal, ...rest } = opts;
  return fetchServePostJson(
    'producing_regions_score_cards_endpoint',
    buildScorecardStyleBody(commodityKey, rest),
    { signal },
  );
};

